import { useMemo } from "react"
import { useQuery } from "urql"

import { DEBOUNCE_DELAY_MS } from "@/constants/common"
import {
  GetInventoryAdjustmentLotProductTestDocument,
  type ProductTest,
  type ProductTestListSuccess,
} from "@/graphql/codegen/graphql"
import { calculateRemainingShelfLife } from "@/utils/product"
import useDebounce from "@/utils/useDebounce"

interface UseGetAdjustmentLotProductTestParams {
  productId: number
  lotNumber: string
  batchNumber: string
}

interface UseGetAdjustmentLotProductTestReturn {
  productTest: ProductTest | null
  shouldShowUntestedLotWarning: boolean
  remainingShelfLife: number | null
}

export const useGetAdjustmentLotProductTest = ({
  productId,
  lotNumber,
  batchNumber,
}: UseGetAdjustmentLotProductTestParams): UseGetAdjustmentLotProductTestReturn => {
  const filter = useMemo(() => ({ productId, lotNumber, batchNumber }), [productId, lotNumber, batchNumber])
  const debouncedFilter = useDebounce(filter, DEBOUNCE_DELAY_MS)

  const [{ data, fetching }] = useQuery({
    query: GetInventoryAdjustmentLotProductTestDocument,
    variables: { input: { filter: debouncedFilter } },
    pause: !debouncedFilter.lotNumber,
  })

  const isFilterChanged =
    filter.lotNumber !== debouncedFilter.lotNumber || filter.batchNumber !== debouncedFilter.batchNumber

  const productTest =
    data?.productTest.list.__typename === "ProductTestListSuccess"
      ? ((data.productTest.list as ProductTestListSuccess).productTests[0] ?? null)
      : null

  const shouldShowUntestedLotWarning = !!lotNumber && !isFilterChanged && !fetching && !productTest

  const remainingShelfLife = calculateRemainingShelfLife(productTest?.manufacturingDate, productTest?.expirationDate)

  return { productTest, remainingShelfLife, shouldShowUntestedLotWarning }
}
